/**
 * WorkDetailsReport.js
 * 업무내역 리포트 페이지
 * [업무 투입시간], [To-Do 리스트] 작성에 따라 자동으로 생성되는 리포트 기능
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  InputAdornment,
  Chip,
  Autocomplete,
  Snackbar,
  Alert,
} from '@mui/material';
import { Download, Calendar, RefreshCw, Filter, ChevronUp, ChevronDown, Copy } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import axios from 'axios';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { ko } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import apiConfig from '../config/api';

const API_BASE_URL = apiConfig.V1;

const WorkDetailsReport = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [originalReportData, setOriginalReportData] = useState([]); // 필터링 전 원본 데이터
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  
  // 필터 상태
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedContracts, setSelectedContracts] = useState([]);
  
  // 인라인 편집 상태
  const [editingCell, setEditingCell] = useState(null); // { type: 'done' | 'todo', itemIndex, taskIndex, field: 'content' }
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false); // 저장 중 상태
  const saveTimeoutRef = useRef(null); // debounce를 위한 timeout ref
  const originalEditValueRef = useRef(''); // 원본 값 저장 (변경사항 체크용)
  
  // Snackbar 상태
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // 업체명, 계약명 목록 추출
  const companyOptions = useMemo(() => {
    const companies = new Set();
    originalReportData.forEach(item => {
      const companyName = item.company_name_kr || item.company_code || '';
      if (companyName) {
        companies.add(companyName);
      }
    });
    return Array.from(companies).sort();
  }, [originalReportData]);
  
  const contractOptions = useMemo(() => {
    const contracts = new Set();
    originalReportData.forEach(item => {
      const contractName = item.contract_title || item.contract_id || '';
      if (contractName) {
        contracts.add(contractName);
      }
    });
    return Array.from(contracts).sort();
  }, [originalReportData]);

  // 리포트 데이터 조회
  const fetchReportData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/reports/work-details`, {
        params: {
          start_date: startDate,
          end_date: endDate,
        },
      });
      setOriginalReportData(response.data || []);
      setReportData(response.data || []);
      // 필터 초기화
      setSelectedCompanies([]);
      setSelectedContracts([]);
    } catch (error) {
      console.error('리포트 데이터 조회 실패:', error);
      alert('리포트 데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 필터링된 데이터 계산
  useEffect(() => {
    let filtered = [...originalReportData];
    
    if (selectedCompanies.length > 0) {
      filtered = filtered.filter(item => {
        const companyName = item.company_name_kr || item.company_code || '';
        return selectedCompanies.includes(companyName);
      });
    }
    
    if (selectedContracts.length > 0) {
      filtered = filtered.filter(item => {
        const contractName = item.contract_title || item.contract_id || '';
        return selectedContracts.includes(contractName);
      });
    }
    
    setReportData(filtered);
  }, [selectedCompanies, selectedContracts, originalReportData]);

  useEffect(() => {
    fetchReportData();
  }, []);

  // 날짜 증감 유틸리티
  const adjustDateByDays = (dateStr, delta) => {
    const baseDate = dateStr ? new Date(dateStr) : new Date();
    if (Number.isNaN(baseDate.getTime())) return dateStr || format(new Date(), 'yyyy-MM-dd');
    baseDate.setDate(baseDate.getDate() + delta);
    return format(baseDate, 'yyyy-MM-dd');
  };

  const handleRangeDateStep = (target, delta) => {
    const currentDate = target === 'start' ? startDate : endDate;
    const newDate = adjustDateByDays(currentDate, delta);
    if (target === 'start') {
      setStartDate(newDate);
    } else {
      setEndDate(newDate);
    }
  };

  // 인라인 편집: 셀 클릭
  const handleCellClick = (type, itemIndex, taskIndex, currentValue) => {
    const value = currentValue || '';
    setEditingCell({ type, itemIndex, taskIndex, field: 'content' });
    setEditValue(value);
    originalEditValueRef.current = value; // 원본 값 저장
    // 기존 timeout 취소
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  };

  // 인라인 편집: 저장
  const handleCellSave = useCallback(async (type, itemIndex, taskIndex, skipCheck = false) => {
    if (!editingCell) {
      console.warn('handleCellSave: editingCell이 없습니다.');
      return;
    }
    
    const item = reportData[itemIndex];
    if (!item) {
      console.warn('handleCellSave: item을 찾을 수 없습니다.', { itemIndex, type });
      return;
    }
    
    const currentEditValue = editValue;
    
    // 변경사항이 없으면 저장하지 않음
    if (!skipCheck && currentEditValue.trim() === originalEditValueRef.current.trim()) {
      setEditingCell(null);
      setEditValue('');
      return;
    }
    
    setSaving(true);
    try {
      console.log('저장 시작:', { type, itemIndex, taskIndex, currentEditValue: currentEditValue.substring(0, 50) });
      if (type === 'done') {
        // Done 항목은 timelog 수정
        const task = item.done_tasks[taskIndex];
        if (!task || !task.timelog_id) return;
        
        const updatedContent = currentEditValue.trim();
        
        // 로컬 상태 업데이트
        const updatedData = [...reportData];
        const updatedTask = { ...task, content: updatedContent };
        if (updatedTask.display_text) {
          // display_text 재생성
          const dateStr = updatedTask.date || '';
          const jobCategory = updatedTask.job_category || '';
          updatedTask.display_text = `(${dateStr})${jobCategory ? ` [${jobCategory}]` : ''} ${updatedContent}`.trim();
        }
        updatedData[itemIndex].done_tasks[taskIndex] = updatedTask;
        setReportData(updatedData);
        
        // 원본 데이터도 업데이트
        const updatedOriginalData = [...originalReportData];
        const originalItemIndex = updatedOriginalData.findIndex(
          origItem => origItem.company_code === item.company_code && origItem.contract_id === item.contract_id
        );
        if (originalItemIndex >= 0) {
          const originalTaskIndex = updatedOriginalData[originalItemIndex].done_tasks.findIndex(
            t => t.timelog_id === task.timelog_id
          );
          if (originalTaskIndex >= 0) {
            updatedOriginalData[originalItemIndex].done_tasks[originalTaskIndex] = updatedTask;
            setOriginalReportData(updatedOriginalData);
          }
        }
        
        // 백엔드 API 호출 (timelog 수정)
        // content를 input_description으로 저장 (기존 형식 유지)
        // log_date와 input_minute는 필수 필드이므로 기존 값 사용
        let logDate = task.log_date;
        if (!logDate && task.date) {
          // task.date가 문자열인 경우 (YYYY/MM/DD 형식) 날짜 형식으로 변환
          const dateParts = task.date.split('/');
          if (dateParts.length === 3) {
            logDate = `${dateParts[0]}-${dateParts[1].padStart(2, '0')}-${dateParts[2].padStart(2, '0')}`;
          }
        }
        // log_date가 없으면 에러
        if (!logDate) {
          throw new Error('날짜 정보를 찾을 수 없습니다.');
        }
        const inputMinute = task.input_minute || 0;
        
        console.log('Timelog 업데이트 요청:', {
          timelog_id: task.timelog_id,
          log_date: logDate,
          input_minute: inputMinute,
          input_description: updatedContent,
          company_code: item.company_code,
          contract_id: item.contract_id,
          job_category: task.job_category,
        });
        await axios.put(`${API_BASE_URL}/timelogs/${task.timelog_id}`, {
          log_date: logDate,
          input_minute: inputMinute,
          input_description: updatedContent,
          company_code: item.company_code,
          contract_id: item.contract_id,
          job_category: task.job_category,
        });
        console.log('Timelog 업데이트 성공');
      } else if (type === 'todo') {
        // To-Do 항목은 todo 수정
        const task = item.todo_tasks[taskIndex];
        if (!task) return;
        
        const updatedContent = currentEditValue.trim();
        
        // 로컬 상태 업데이트
        const updatedData = [...reportData];
        const updatedTask = { ...task, content: updatedContent, note_1: updatedContent };
        if (updatedTask.display_text) {
          // display_text 재생성
          const jobCategory = updatedTask.job_category || '';
          updatedTask.display_text = `${jobCategory ? `[${jobCategory}] ` : ''}${updatedContent}`.trim();
        }
        updatedData[itemIndex].todo_tasks[taskIndex] = updatedTask;
        setReportData(updatedData);
        
        // 원본 데이터도 업데이트
        const updatedOriginalData = [...originalReportData];
        const originalItemIndex = updatedOriginalData.findIndex(
          origItem => origItem.company_code === item.company_code && origItem.contract_id === item.contract_id
        );
        if (originalItemIndex >= 0) {
          const originalTaskIndex = updatedOriginalData[originalItemIndex].todo_tasks.findIndex(
            t => t.todo_id === task.todo_id
          );
          if (originalTaskIndex >= 0) {
            updatedOriginalData[originalItemIndex].todo_tasks[originalTaskIndex] = updatedTask;
            setOriginalReportData(updatedOriginalData);
          }
        }
        
        // 백엔드 API 호출
        if (task.todo_id) {
          console.log('Todo 업데이트 요청:', {
            todo_id: task.todo_id,
            note_1: updatedContent,
            company_code: item.company_code,
            contract_id: item.contract_id,
            job_category: task.job_category,
          });
          await axios.put(`${API_BASE_URL}/todos/${task.todo_id}`, {
            note_1: updatedContent,
            company_code: item.company_code,
            contract_id: item.contract_id,
            job_category: task.job_category,
          });
          console.log('Todo 업데이트 성공');
          // RightSidebar 갱신을 위한 이벤트 발생
          window.dispatchEvent(new CustomEvent('todoListUpdated'));
        } else {
          console.warn('Todo 업데이트 실패: todo_id가 없습니다.', task);
        }
      }
      
      setEditingCell(null);
      setEditValue('');
      originalEditValueRef.current = ''; // 원본 값 초기화
    } catch (error) {
      console.error('저장 실패 - 전체 에러 객체:', error);
      console.error('저장 실패 - 에러 응답:', error.response);
      console.error('저장 실패 - 에러 요청:', error.request);
      console.error('저장 실패 - 에러 메시지:', error.message);
      
      // 에러 메시지 추출
      let errorMessage = '알 수 없는 오류가 발생했습니다.';
      
      if (error.response) {
        // 서버 응답이 있는 경우
        const data = error.response.data;
        console.error('에러 응답 데이터:', data);
        
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data?.detail) {
          if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (Array.isArray(data.detail)) {
            // 배열인 경우 각 항목을 문자열로 변환
            errorMessage = data.detail.map(item => 
              typeof item === 'string' ? item : JSON.stringify(item)
            ).join(', ');
          } else {
            errorMessage = JSON.stringify(data.detail);
          }
        } else if (data?.message) {
          errorMessage = typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
        } else if (data) {
          errorMessage = typeof data === 'string' ? data : JSON.stringify(data);
        } else {
          errorMessage = `서버 오류 (${error.response.status} ${error.response.statusText || ''})`;
        }
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.request) {
        errorMessage = '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.';
      }
      
      alert(`저장에 실패했습니다.\n\n${errorMessage}`);
      // 에러 발생 시 편집 모드 유지
    } finally {
      setSaving(false);
    }
  }, [editingCell, editValue, reportData, originalReportData]);

  // debounce를 사용한 자동 저장
  useEffect(() => {
    if (!editingCell || saving) return;
    
    // 기존 timeout 취소
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // 변경사항이 없으면 저장하지 않음
    if (editValue.trim() === originalEditValueRef.current.trim()) {
      return;
    }
    
    // 1초 후 자동 저장
    saveTimeoutRef.current = setTimeout(() => {
      handleCellSave(
        editingCell.type,
        editingCell.itemIndex,
        editingCell.taskIndex,
        true // skipCheck = true (이미 체크했으므로)
      );
    }, 1000);
    
    // cleanup
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [editValue, editingCell, saving, handleCellSave]); // editValue가 변경될 때마다 실행

  // 인라인 편집: 취소
  const handleCellCancel = () => {
    // 기존 timeout 취소
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    setEditingCell(null);
    setEditValue('');
    originalEditValueRef.current = '';
  };

  // ul 단위 복사 함수
  const handleCopyTasks = useCallback((tasks, type) => {
    if (!tasks || tasks.length === 0) {
      setSnackbarMessage('복사할 항목이 없습니다.');
      setSnackbarOpen(true);
      return;
    }

    // 마크다운 형식으로 변환
    const markdownText = tasks.map((task, index) => {
      const content = task.display_text || task.content || task.note_1 || '-';
      return `- ${content}`;
    }).join('\n');

    // 클립보드에 복사
    navigator.clipboard.writeText(markdownText).then(() => {
      setSnackbarMessage('복사되었습니다');
      setSnackbarOpen(true);
    }).catch((err) => {
      console.error('복사 실패:', err);
      setSnackbarMessage('복사에 실패했습니다.');
      setSnackbarOpen(true);
    });
  }, []);

  // 엑셀 다운로드
  const handleDownloadExcel = () => {
    if (reportData.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    // 엑셀 데이터 준비
    const excelData = [];
    
    reportData.forEach((item) => {
      // Done 항목들
      item.done_tasks.forEach((task) => {
        excelData.push({
          '업체명': item.company_name_kr || item.company_code || '',
          '계약명': item.contract_title || item.contract_id || '',
          '구분': 'Done',
          '내용': task.display_text || task.content || '',
          '날짜': task.date || '',
          '업무구분': task.job_category || '',
          '투입시간(분)': task.input_minute || 0,
        });
      });
      
      // To-Do 항목들
      item.todo_tasks.forEach((task) => {
        excelData.push({
          '업체명': item.company_name_kr || item.company_code || '',
          '계약명': item.contract_title || item.contract_id || '',
          '구분': 'To-Do',
          '내용': task.display_text || task.content || '',
          '날짜': '',
          '업무구분': task.job_category || '',
          '투입시간(분)': '',
        });
      });
    });

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // 컬럼 너비 설정
    const colWidths = [
      { wch: 20 },  // 업체명
      { wch: 25 },  // 계약명
      { wch: 10 },  // 구분
      { wch: 50 },  // 내용
      { wch: 12 },  // 날짜
      { wch: 15 },  // 업무구분
      { wch: 15 },  // 투입시간(분)
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, '업무내역 리포트');
    
    // 파일명 생성
    const employeeName = localStorage.getItem('employeeName') || '사용자';
    const now = new Date();
    const dateStr = format(now, 'yyyyMMdd');
    const timeStr = format(now, 'HHmmss');
    const fileName = `OnTimeLog_${employeeName}_업무내역리포트_${dateStr}_${timeStr}.xlsx`;
    
    // 파일 다운로드
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Box
      sx={{
        p: isMobileView ? 2 : 3,
        minHeight: '100vh',
        backgroundColor: colors.primaryBg,
      }}
    >
      {/* 헤더 */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="caption"
          sx={{
            color: colors.secondaryText,
            fontSize: '0.75rem',
            mb: 1,
            display: 'block',
          }}
        >
          [업무 투입시간], [To-Do 리스트] 작성에 따라 자동으로 생성되는 리포트 기능
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 700,
              color: colors.primaryText,
              fontSize: isMobileView ? '1.5rem' : '20px',
            }}
          >
            업무내역 리포트
          </Typography>
        </Box>
      </Box>

      {/* 필터 및 날짜 필터 */}
      <Card
        sx={{
          mb: 3,
          backgroundColor: colors.secondaryBg,
          border: `1px solid ${colors.primaryBorder}`,
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            {/* 업체명, 계약명 필터 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 0.5 }}>
                <Filter size={18} style={{ color: colors.secondaryText }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: colors.secondaryText,
                    fontWeight: 500,
                  }}
                >
                  필터
                </Typography>
              </Box>
              <Autocomplete
                multiple
                size="small"
                options={companyOptions}
                value={selectedCompanies}
                onChange={(event, newValue) => {
                  setSelectedCompanies(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="업체명"
                    placeholder="업체명 선택"
                    sx={{
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: colors.primaryBg,
                        '& fieldset': {
                          borderColor: colors.primaryBorder,
                        },
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{
                        backgroundColor: colors.accent,
                        color: '#fff',
                        '& .MuiChip-deleteIcon': {
                          color: '#fff',
                        },
                      }}
                    />
                  ))
                }
              />
              <Autocomplete
                multiple
                size="small"
                options={contractOptions}
                value={selectedContracts}
                onChange={(event, newValue) => {
                  setSelectedContracts(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="계약명"
                    placeholder="계약명 선택"
                    sx={{
                      minWidth: 200,
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: colors.primaryBg,
                        '& fieldset': {
                          borderColor: colors.primaryBorder,
                        },
                      },
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      size="small"
                      sx={{
                        backgroundColor: colors.accent,
                        color: '#fff',
                        '& .MuiChip-deleteIcon': {
                          color: '#fff',
                        },
                      }}
                    />
                  ))
                }
              />
            </Box>
            
            {/* 조회 기간 선택 뱃지 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: colors.secondaryText, fontSize: '13px', mr: 0.5 }}>
                조회 기간:
              </Typography>
              <Chip
                label="일주일"
                onClick={() => {
                  const today = new Date();
                  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
                  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
                  setStartDate(format(weekStart, 'yyyy-MM-dd'));
                  setEndDate(format(weekEnd, 'yyyy-MM-dd'));
                  fetchReportData();
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: startDate && endDate && 
                    format(new Date(startDate), 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') &&
                    format(new Date(endDate), 'yyyy-MM-dd') === format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
                    ? colors.accent
                    : isDark ? '#2A2B30' : '#F0F0F0',
                  color: startDate && endDate && 
                    format(new Date(startDate), 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') &&
                    format(new Date(endDate), 'yyyy-MM-dd') === format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
                    ? '#FFFFFF'
                    : colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
              <Chip
                label="한달"
                onClick={() => {
                  const today = new Date();
                  const monthStart = startOfMonth(today);
                  const monthEnd = endOfMonth(today);
                  setStartDate(format(monthStart, 'yyyy-MM-dd'));
                  setEndDate(format(monthEnd, 'yyyy-MM-dd'));
                  fetchReportData();
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: startDate && endDate && 
                    format(new Date(startDate), 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
                    format(new Date(endDate), 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd')
                    ? colors.accent
                    : isDark ? '#2A2B30' : '#F0F0F0',
                  color: startDate && endDate && 
                    format(new Date(startDate), 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
                    format(new Date(endDate), 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd')
                    ? '#FFFFFF'
                    : colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
              <Chip
                label="전체"
                onClick={() => {
                  // 전체는 최근 1년으로 설정
                  const today = new Date();
                  const oneYearAgo = subDays(today, 365);
                  setStartDate(format(oneYearAgo, 'yyyy-MM-dd'));
                  setEndDate(format(today, 'yyyy-MM-dd'));
                  fetchReportData();
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: isDark ? '#2A2B30' : '#F0F0F0',
                  color: colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
            </Box>
            
            {/* 날짜 필터 */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2, 
              flexWrap: 'nowrap',
              textAlign: 'left',
              border: 'none'
            }}>
              <Typography
                variant="body2"
                sx={{
                  color: colors.secondaryText,
                  fontWeight: 500,
                  mr: 0.5,
                }}
              >
                업무일자 기준 조회
              </Typography>
              <TextField
                type="date"
                label="시작일"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: -0.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRangeDateStep('start', 1)}
                          sx={{ p: 0.25, color: colors.secondaryText }}
                          aria-label="시작일 하루 증가"
                        >
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRangeDateStep('start', -1)}
                          sx={{ p: 0.25, color: colors.secondaryText }}
                          aria-label="시작일 하루 감소"
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 170,
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: colors.primaryBg,
                    '& fieldset': {
                      borderColor: colors.primaryBorder,
                    },
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                    opacity: 0.7,
                  },
                }}
                inputProps={{
                  sx: {
                    color: colors.primaryText,
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  }
                }}
              />
              <Typography variant="body2" sx={{ color: colors.secondaryText, flexShrink: 0 }}>
                ~
              </Typography>
              <TextField
                type="date"
                label="종료일"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ mr: -0.5 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRangeDateStep('end', 1)}
                          sx={{ p: 0.25, color: colors.secondaryText }}
                          aria-label="종료일 하루 증가"
                        >
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRangeDateStep('end', -1)}
                          sx={{ p: 0.25, color: colors.secondaryText }}
                          aria-label="종료일 하루 감소"
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: 170,
                  flexShrink: 0,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: colors.primaryBg,
                    '& fieldset': {
                      borderColor: colors.primaryBorder,
                    },
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                    opacity: 0.7,
                  },
                }}
                inputProps={{
                  sx: {
                    color: colors.primaryText,
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  }
                }}
              />
              <Button
                variant="contained"
                startIcon={<RefreshCw size={18} />}
                onClick={fetchReportData}
                disabled={loading}
                sx={{
                  backgroundColor: colors.accent,
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    opacity: 0.9,
                  },
                }}
              >
                조회
              </Button>
              <Tooltip title="엑셀 다운로드">
                <span>
                  <IconButton
                    onClick={handleDownloadExcel}
                    disabled={reportData.length === 0}
                    sx={{
                      color: colors.accent,
                      border: `1px solid ${colors.accent}`,
                      '&:hover': {
                        borderColor: colors.accent,
                        backgroundColor: isDark ? 'rgba(138, 124, 255, 0.1)' : 'rgba(138, 124, 255, 0.05)',
                      },
                      '&.Mui-disabled': {
                        borderColor: colors.secondaryText,
                        color: colors.secondaryText,
                        opacity: 0.5,
                      },
                    }}
                  >
                    <Download size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* 리포트 테이블 */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ color: colors.secondaryText }}>
            데이터를 불러오는 중...
          </Typography>
        </Box>
      ) : reportData.length === 0 ? (
        <Card
          sx={{
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
          }}
        >
          <CardContent>
            <Typography variant="body2" sx={{ color: colors.secondaryText, textAlign: 'center', py: 4 }}>
              조회된 데이터가 없습니다.
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: colors.secondaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            maxHeight: 'calc(100vh - 400px)',
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: colors.secondaryBg,
                    color: colors.primaryText,
                    fontWeight: 600,
                    borderBottom: `1px solid ${colors.primaryBorder}`,
                    minWidth: 150, // 120 -> 150으로 30px 증가
                  }}
                >
                  업체명
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: colors.secondaryBg,
                    color: colors.primaryText,
                    fontWeight: 600,
                    borderBottom: `1px solid ${colors.primaryBorder}`,
                    minWidth: 130, // 100 -> 130으로 30px 증가
                  }}
                >
                  계약명
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    backgroundColor: colors.secondaryBg,
                    color: colors.primaryText,
                    fontWeight: 600,
                    borderBottom: `1px solid ${colors.primaryBorder}`,
                    minWidth: 350, // 400 -> 350으로 50px 감소
                    textAlign: 'left',
                    paddingLeft: '30px',
                    paddingRight: '30px',
                  }}
                >
                  Done
                </TableCell>
                <TableCell
                  align="left"
                  sx={{
                    backgroundColor: colors.secondaryBg,
                    color: colors.primaryText,
                    fontWeight: 600,
                    borderBottom: `1px solid ${colors.primaryBorder}`,
                    minWidth: 400, // 300 -> 400으로 늘림
                    textAlign: 'left',
                    pl: '30px',
                    pr: '30px',
                  }}
                >
                  To-Do
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.map((item, index) => (
                <TableRow
                  key={`${item.company_code}-${item.contract_id}-${index}`}
                  sx={{
                    '&:hover': {
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      color: colors.primaryText,
                      borderBottom: `1px solid ${colors.primaryBorder}`,
                      verticalAlign: 'top',
                      pt: 2,
                    }}
                  >
                    {item.company_name_kr || item.company_code || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      color: colors.primaryText,
                      borderBottom: `1px solid ${colors.primaryBorder}`,
                      verticalAlign: 'top',
                      pt: 2,
                    }}
                  >
                    {item.contract_title || item.contract_id || '-'}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      color: colors.primaryText,
                      borderBottom: `1px solid ${colors.primaryBorder}`,
                      verticalAlign: 'top',
                      pt: 2,
                      textAlign: 'left',
                    }}
                  >
                    {item.done_tasks.length === 0 ? (
                      <Typography variant="body2" sx={{ color: colors.secondaryText, textAlign: 'left' }}>
                        -
                      </Typography>
                    ) : (
                      <Box sx={{ position: 'relative' }}>
                        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: 'none', textAlign: 'left' }}>
                          {item.done_tasks.map((task, taskIndex) => {
                          const isEditing = editingCell?.type === 'done' && 
                                          editingCell?.itemIndex === index && 
                                          editingCell?.taskIndex === taskIndex;
                          const currentValue = task.content || '';
                          
                          return (
                            <Box
                              component="li"
                              key={taskIndex}
                              sx={{
                                mb: 1,
                                '&:last-child': { mb: 0 },
                                textAlign: 'left',
                              }}
                            >
                              {isEditing ? (
                                <Box sx={{ position: 'relative' }}>
                                  <TextField
                                    value={editValue}
                                    onChange={(e) => {
                                      // debounce timeout 취소
                                      if (saveTimeoutRef.current) {
                                        clearTimeout(saveTimeoutRef.current);
                                      }
                                      setEditValue(e.target.value);
                                    }}
                                    onBlur={() => {
                                      // onBlur 시 즉시 저장 (debounce 대기 중이면 취소하고 바로 저장)
                                      if (saveTimeoutRef.current) {
                                        clearTimeout(saveTimeoutRef.current);
                                        saveTimeoutRef.current = null;
                                      }
                                      handleCellSave('done', index, taskIndex);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        // Enter 시 즉시 저장
                                        if (saveTimeoutRef.current) {
                                          clearTimeout(saveTimeoutRef.current);
                                          saveTimeoutRef.current = null;
                                        }
                                        handleCellSave('done', index, taskIndex);
                                      }
                                      if (e.key === 'Escape') {
                                        handleCellCancel();
                                      }
                                    }}
                                    autoFocus
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    disabled={saving}
                                    sx={{
                                      fontSize: '0.875rem',
                                      '& .MuiInputBase-input': {
                                        fontSize: '0.875rem',
                                        lineHeight: 1.6,
                                      },
                                    }}
                                  />
                                  {saving && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 8,
                                        color: colors.accent,
                                        fontSize: '0.7rem',
                                      }}
                                    >
                                      저장 중...
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  onClick={() => handleCellClick('done', index, taskIndex, currentValue)}
                                  sx={{
                                    color: colors.primaryText,
                                    fontSize: '0.875rem',
                                    lineHeight: 1.6,
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                      borderRadius: 1,
                                      px: 0.5,
                                    },
                                  }}
                                >
                                  {task.display_text || task.content || '-'}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                        </Box>
                        <Tooltip title="복사">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyTasks(item.done_tasks, 'done')}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              color: colors.secondaryText,
                              '&:hover': {
                                color: colors.accent,
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                              },
                            }}
                          >
                            <Copy size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      color: colors.primaryText,
                      borderBottom: `1px solid ${colors.primaryBorder}`,
                      verticalAlign: 'top',
                      pt: 2,
                      textAlign: 'left',
                    }}
                  >
                    {item.todo_tasks.length === 0 ? (
                      <Typography variant="body2" sx={{ color: colors.secondaryText, textAlign: 'left' }}>
                        -
                      </Typography>
                    ) : (
                      <Box sx={{ position: 'relative' }}>
                        <Box component="ul" sx={{ m: 0, pl: 2, listStyle: 'none', textAlign: 'left' }}>
                          {item.todo_tasks.map((task, taskIndex) => {
                          const isEditing = editingCell?.type === 'todo' && 
                                          editingCell?.itemIndex === index && 
                                          editingCell?.taskIndex === taskIndex;
                          const currentValue = task.content || task.note_1 || '';
                          
                          return (
                            <Box
                              component="li"
                              key={taskIndex}
                              sx={{
                                mb: 1,
                                '&:last-child': { mb: 0 },
                                textAlign: 'left',
                              }}
                            >
                              {isEditing ? (
                                <Box sx={{ position: 'relative' }}>
                                  <TextField
                                    value={editValue}
                                    onChange={(e) => {
                                      // debounce timeout 취소
                                      if (saveTimeoutRef.current) {
                                        clearTimeout(saveTimeoutRef.current);
                                      }
                                      setEditValue(e.target.value);
                                    }}
                                    onBlur={() => {
                                      // onBlur 시 즉시 저장 (debounce 대기 중이면 취소하고 바로 저장)
                                      if (saveTimeoutRef.current) {
                                        clearTimeout(saveTimeoutRef.current);
                                        saveTimeoutRef.current = null;
                                      }
                                      handleCellSave('todo', index, taskIndex);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        // Enter 시 즉시 저장
                                        if (saveTimeoutRef.current) {
                                          clearTimeout(saveTimeoutRef.current);
                                          saveTimeoutRef.current = null;
                                        }
                                        handleCellSave('todo', index, taskIndex);
                                      }
                                      if (e.key === 'Escape') {
                                        handleCellCancel();
                                      }
                                    }}
                                    autoFocus
                                    size="small"
                                    fullWidth
                                    multiline
                                    rows={2}
                                    disabled={saving}
                                    sx={{
                                      fontSize: '0.875rem',
                                      '& .MuiInputBase-input': {
                                        fontSize: '0.875rem',
                                        lineHeight: 1.6,
                                      },
                                    }}
                                  />
                                  {saving && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 8,
                                        color: colors.accent,
                                        fontSize: '0.7rem',
                                      }}
                                    >
                                      저장 중...
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography
                                  variant="body2"
                                  onClick={() => handleCellClick('todo', index, taskIndex, currentValue)}
                                  sx={{
                                    color: colors.primaryText,
                                    fontSize: '0.875rem',
                                    lineHeight: 1.6,
                                    textAlign: 'left',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                                      borderRadius: 1,
                                      px: 0.5,
                                    },
                                  }}
                                >
                                  {task.display_text || task.content || '-'}
                                </Typography>
                              )}
                            </Box>
                          );
                        })}
                        </Box>
                        <Tooltip title="복사">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyTasks(item.todo_tasks, 'todo')}
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              color: colors.secondaryText,
                              '&:hover': {
                                color: colors.accent,
                                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
                              },
                            }}
                          >
                            <Copy size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      
      {/* Snackbar for copy notification */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{
            backgroundColor: colors.accent,
            color: '#fff',
            '& .MuiAlert-icon': {
              color: '#fff',
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WorkDetailsReport;
